import { useEffect, useRef } from "react";

interface TurnstileApi {
  render(
    container: HTMLElement,
    options: {
      sitekey: string;
      action: string;
      appearance: "always";
      size: "flexible";
      theme: "light";
      callback: (token: string) => void;
      "error-callback": () => void;
      "expired-callback": () => void;
    },
  ): string;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | undefined;

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  const pending = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-stpwin-turnstile="true"]',
    );
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("load failed")),
        {
          once: true,
        },
      );
      return;
    }
    const script = document.createElement("script");
    script.async = true;
    script.defer = true;
    script.dataset.stpwinTurnstile = "true";
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener("error", () => reject(new Error("load failed")), {
      once: true,
    });
    document.head.appendChild(script);
  });
  const retryable = pending.catch((error: unknown) => {
    document
      .querySelector<HTMLScriptElement>(
        'script[data-stpwin-turnstile="true"]',
      )
      ?.remove();
    scriptPromise = undefined;
    throw error;
  });
  scriptPromise = retryable;
  return retryable;
}

export function TurnstileWidget({
  onError,
  onToken,
  siteKey,
}: {
  onError: () => void;
  onToken: (token: string) => void;
  siteKey: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    let active = true;
    void loadTurnstile()
      .then(() => {
        if (!active || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: "login",
          appearance: "always",
          size: "flexible",
          theme: "light",
          callback: onToken,
          "error-callback": onError,
          "expired-callback": () => onToken(""),
        });
      })
      .catch(onError);
    return () => {
      active = false;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
  }, [onError, onToken, siteKey]);

  return (
    <div
      aria-label="Human verification"
      className="turnstile-widget"
      data-turnstile="login"
      ref={containerRef}
    />
  );
}
